// MoveTeamsPanel — "Move teams onto reports", on Account (v0.339.0).
// OWNER / ADMIN ONLY (the server refuses anyone else with 403; the panel then
// draws nothing, since there is nothing they could do with it).
//
// Brett: "I can't run sql." and "Location shows nothing. Do it properly. Just
// make sure all the same people can access the Grill'd reports when you are
// done." A team now belongs to each report. Until this runs, every report
// still shows its location's old team; this panel moves it, once.
//
// GET  /api/team-move  → { preview, lastRun }
//      preview.locations[]: { name, people[{name, company, sideWords, ownChat}],
//        reports[{ id, name, adds[], alreadyOn[], routes{team,link,seat} }], noReports }
// POST /api/team-move  → { result: 'moved'|'nothing', added, retired, reports[] }
//      409 → { error, diff[{ report, location, lost[], gained[] }] } — the server
//      rolled the whole move back.
//
// States, each in its own words: reading, could not read (Try again; offline
// said so), nothing to move (with the last run), the preview, running, moved
// ("Same people, every report" + each report), rolled back (who would have
// gained or lost what), run failed (nothing moved, Try again).

import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiSend, fmtDate } from '../api';
import { theme, card, btnPrimary, btnSecondary } from '../theme';

const ROUTE_WORDS = { team: ['on the team', 'on the team'], link: ['live link', 'live links'], seat: ['client-company seat', 'client-company seats'] };

function errWords(e) {
  if (e && (e.offline || (typeof navigator !== 'undefined' && navigator.onLine === false))) return 'There is no connection, so nothing was read or changed.';
  return (e && e.body && e.body.error) || (e && e.message) || String(e);
}

export default function MoveTeamsPanel() {
  const [state, setState] = useState('loading'); // loading | hidden | failed | ready | running | moved | mismatch | runFailed
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [diff, setDiff] = useState(null);

  const load = useCallback(async () => {
    setState('loading');
    setErr('');
    try {
      const d = await apiGet('/api/team-move', { fresh: true });
      setData(d);
      setState('ready');
    } catch (e) {
      if (e && e.status === 403) { setState('hidden'); return; }
      setErr(errWords(e));
      setState('failed');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function run() {
    const p = data && data.preview;
    if (!p) return;
    if (!window.confirm(`Copy ${p.toAdd} ${p.toAdd === 1 ? 'person' : 'people'} onto their reports and retire the location teams? `
      + 'Who can open each report is checked before and after; any difference undoes the whole move.')) return;
    setState('running');
    setErr('');
    try {
      const out = await apiSend('POST', '/api/team-move', {}, { noQueue: true });
      setResult(out);
      setState('moved');
    } catch (e) {
      if (e && e.status === 409 && e.body && Array.isArray(e.body.diff)) {
        setDiff(e.body.diff);
        setErr(e.body.error || '');
        setState('mismatch');
        return;
      }
      setErr(errWords(e));
      setState('runFailed');
    }
  }

  if (state === 'hidden') return null;

  const quiet = { fontSize: 13, color: theme.textLight };
  const box = (colour) => ({
    padding: 10, borderRadius: 8, fontSize: 13, lineHeight: 1.45,
    border: `1px solid ${colour}`, background: theme.bg, color: theme.text,
  });
  const btnRow = { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 };
  const label = { fontSize: 11, fontWeight: 800, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' };

  let body;
  if (state === 'loading') {
    body = <div style={quiet}>Reading each location's team and who can open its reports…</div>;
  } else if (state === 'failed') {
    body = (
      <div style={box(theme.danger)}>
        The teams could not be read, so nothing is shown: {err}
        <div style={btnRow}><button type="button" style={btnSecondary} onClick={load}>Try again</button></div>
      </div>
    );
  } else if (state === 'moved' && result) {
    body = (
      <div data-state="moved">
        <div style={box(theme.success)}>
          <strong>{result.result === 'nothing' ? 'Nothing to move - ' : ''}Same people, every report.</strong>{' '}
          {result.result === 'moved'
            ? `${result.added} ${result.added === 1 ? 'person was' : 'people were'} put on their reports and ${result.retired} location ${result.retired === 1 ? 'row' : 'rows'} retired. Who can open each report was the same before and after.`
            : 'Every team is already on its reports.'}
        </div>
        <ReportList reports={result.reports || []} />
      </div>
    );
  } else if (state === 'mismatch') {
    body = (
      <div data-state="mismatch">
        <div style={box(theme.danger)}>
          <strong>Nothing was moved.</strong> {err || 'Who can reach a report would have changed.'} The move was undone; these are the differences it found.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {(diff || []).map((d) => (
            <div key={d.reportId} data-part="move-diff" style={{ ...box(theme.border), fontSize: 12 }}>
              <div style={{ fontWeight: 700 }}>{d.location || 'Location'} · {d.report || `Report ${d.reportId}`}</div>
              {d.lost.map((x) => <div key={`l${x}`} style={{ color: theme.danger }}>Would lose: {x}</div>)}
              {d.gained.map((x) => <div key={`g${x}`} style={{ color: theme.warn }}>Would gain: {x}</div>)}
            </div>
          ))}
        </div>
        <div style={btnRow}><button type="button" style={btnSecondary} onClick={load}>Read again</button></div>
      </div>
    );
  } else {
    const p = data.preview;
    const last = data.lastRun;
    const lastLine = last ? (
      <div style={{ ...quiet, fontSize: 12, marginTop: 8 }}>
        Last run {fmtDate(last.runAt)}{last.runBy ? ` by ${last.runBy}` : ''}: {last.result === 'moved' ? 'moved, same people on every report' : last.result === 'rolled-back' ? 'undone, nothing moved' : last.result}.
      </div>
    ) : null;
    if (p.nothingToMove) {
      body = (
        <div data-state="nothing">
          <div style={quiet}>Every location's team is already on its reports. There is nothing to move.</div>
          {lastLine}
        </div>
      );
    } else {
      body = (
        <div data-state={state === 'running' ? 'running' : state === 'runFailed' ? 'run-failed' : 'preview'}>
          <div style={{ ...quiet, marginBottom: 10 }}>
            Each location's people go onto every report there (archived ones too), keeping their side and their own chat. Then the location rows are retired. Below: who goes where, and who can open each report today.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {p.locations.map((loc) => (
              <div key={loc.projectId} data-part="move-location" style={{ border: `1px solid ${theme.border}`, borderRadius: 8, padding: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: theme.text }}>{loc.name}</div>
                <div style={{ ...label, marginTop: 8 }}>Team · {loc.people.length}</div>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 13, color: theme.text }}>
                  {loc.people.map((m) => (
                    <li key={m.contactId} data-part="move-person" style={{ overflowWrap: 'anywhere' }}>
                      {m.name}{m.company ? ` (${m.company})` : ''} · {m.sideWords}{m.ownChat ? ' · This project only' : ''}
                    </li>
                  ))}
                </ul>
                {loc.noReports ? (
                  <div style={{ ...quiet, fontSize: 12, marginTop: 8 }}>No reports here yet: the rows are retired and a new report can copy them.</div>
                ) : (
                  <>
                    <div style={{ ...label, marginTop: 10 }}>Reports · {loc.reports.length}</div>
                    {loc.reports.map((r) => (
                      <div key={r.id} data-part="move-report" style={{ fontSize: 12, color: theme.text, marginTop: 6, overflowWrap: 'anywhere' }}>
                        <div style={{ fontWeight: 700 }}>{r.name || `Report ${r.id}`} <span style={{ color: theme.textLight, fontWeight: 400 }}>· adds {r.adds.length}, already on {r.alreadyOn.length}</span></div>
                        <div style={{ color: theme.textLight }}>
                          Can open it today: {Object.entries(r.routes).map(([k, v]) => `${v.length} ${ROUTE_WORDS[k] ? ROUTE_WORDS[k][v.length === 1 ? 0 : 1] : k}`).join(' · ')}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            ))}
          </div>
          {state === 'runFailed' && (
            <div style={{ ...box(theme.danger), marginTop: 10 }}>The move did not run, so nothing changed: {err}</div>
          )}
          <div style={btnRow}>
            <button
              type="button"
              data-part="move-run"
              style={{ ...btnPrimary, opacity: state === 'running' ? 0.6 : 1 }}
              disabled={state === 'running'}
              onClick={run}
            >{state === 'running' ? 'Moving and checking…' : `Move ${p.toAdd} onto reports`}</button>
            <button type="button" style={btnSecondary} disabled={state === 'running'} onClick={load}>Read again</button>
          </div>
          {lastLine}
        </div>
      );
    }
  }

  return (
    <section data-part="move-teams" data-state={state} style={{ ...card, marginTop: 16 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800, color: theme.text }}>Move teams onto reports</h2>
      <div style={{ ...quiet, fontSize: 12, marginBottom: 12 }}>
        A team now belongs to each report, not its location. Run this once; it checks the same people can open every report afterwards, or undoes itself.
      </div>
      {body}
    </section>
  );
}

function ReportList({ reports }) {
  if (!reports.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 }}>
      {reports.map((r) => (
        <div key={r.reportId} data-part="moved-report" style={{ fontSize: 12, color: theme.text, overflowWrap: 'anywhere' }}>
          <strong>{r.location || 'Location'}</strong> · {r.report || `Report ${r.reportId}`} · {r.team} on the team · {r.routes} {r.routes === 1 ? 'way' : 'ways'} in
        </div>
      ))}
    </div>
  );
}
